import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Divider,
  Avatar,
  Button,
  IconButton,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Email,
  Phone,
  LocationOn,
  Verified,
  Pending,
  Cancel,
  Badge as BadgeIcon,
  Business,
  Person,
  CalendarToday,
  ExpandMore,
  ExpandLess,
  AccountBalance,
} from '@mui/icons-material';

/**
 * Styled Components - Enhanced Design
 */
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 2px 8px rgba(41, 30, 108, 0.1)',
  marginBottom: theme.spacing(3),
  border: '1px solid #e0e0e0',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(41, 30, 108, 0.15)',
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: 'rgba(41, 30, 108, 0.05)',
  borderLeft: `4px solid #291e6c`,
}));

const InfoCard = styled(Card)(({ theme }) => ({
  height: '100%',
  border: '1px solid #e0e0e0',
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(41, 30, 108, 0.1)',
    transform: 'translateY(-2px)',
  },
}));

const StyledTableCell = styled(TableCell)({
  border: 'none',
  padding: '12px 16px',
});

const StyledButton = styled(Button)({
  padding: '8px 20px',
  borderRadius: '8px',
  fontSize: '0.9rem',
  fontWeight: 600,
  textTransform: 'none',
  fontFamily: "'Oswald', sans-serif",
});

const InfoRow = ({ icon, label, value }) => (
  <TableRow sx={{ '&:hover': { backgroundColor: 'rgba(41, 30, 108, 0.02)' } }}>
    <StyledTableCell sx={{ width: '50px' }}>
      <Box sx={{ color: '#291e6c', display: 'flex', alignItems: 'center' }}>
        {icon}
      </Box>
    </StyledTableCell>
    <StyledTableCell>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {label}
      </Typography>
    </StyledTableCell>
    <StyledTableCell>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value || 'N/A'}
      </Typography>
    </StyledTableCell>
  </TableRow>
);

const OfficeBearerCard = ({ title, data, icon, expanded, onToggle }) => (
  <StyledPaper>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: expanded ? 2 : 0,
        cursor: 'pointer',
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar sx={{ backgroundColor: '#291e6c', width: 48, height: 48 }}>
          {icon}
        </Avatar>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#291e6c',
              fontFamily: "'Oswald', sans-serif",
            }}
          >
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data?.fullName || 'Not Available'}
          </Typography>
        </Box>
      </Box>
      <IconButton>
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
    </Box>

    <Collapse in={expanded}>
      <Divider sx={{ mb: 2 }} />
      <TableContainer>
        <Table>
          <TableBody>
            <InfoRow
              icon={<Email fontSize="small" />}
              label="Email"
              value={data?.email}
            />
            <InfoRow
              icon={<Phone fontSize="small" />}
              label="Mobile"
              value={data?.mobile}
            />
            <InfoRow
              icon={<LocationOn fontSize="small" />}
              label="Address"
              value={data?.address}
            />
          </TableBody>
        </Table>
      </TableContainer>
    </Collapse>
  </StyledPaper>
);

const ProfileSection = ({ associationData, onUpdate }) => {
  const [expandedSections, setExpandedSections] = useState({
    president: false,
    secretary: false,
    treasurer: false,
  });

  if (!associationData) {
    return (
      <StyledPaper>
        <Typography variant="body1" color="text.secondary" align="center">
          No profile data available
        </Typography>
      </StyledPaper>
    );
  }

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const getVerificationStatus = (status) => {
    switch (status) {
      case 'approved':
        return { label: 'Approved', icon: <Verified />, color: 'success' };
      case 'pending':
        return { label: 'Pending', icon: <Pending />, color: 'warning' };
      case 'rejected':
        return { label: 'Rejected', icon: <Cancel />, color: 'error' };
      default:
        return { label: status, icon: <Pending />, color: 'default' };
    }
  };

  const verificationStatus = getVerificationStatus(
    associationData.verificationStatus
  );

  return (
    <Box>
      {/* Main Profile Card */}
      <StyledPaper>
        <SectionHeader>
          <Business sx={{ color: '#291e6c', fontSize: 32 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#291e6c',
              fontFamily: "'Oswald', sans-serif",
            }}
          >
            Association Profile
          </Typography>
        </SectionHeader>

        <Grid container spacing={3}>
          {/* Association Info */}
          <Grid item xs={12} md={8}>
            <TableContainer>
              <Table>
                <TableBody>
                  <InfoRow
                    icon={<Business fontSize="small" />}
                    label="Association Name"
                    value={associationData.name}
                  />
                  <InfoRow
                    icon={<LocationOn fontSize="small" />}
                    label="State"
                    value={associationData.state}
                  />
                  <InfoRow
                    icon={<BadgeIcon fontSize="small" />}
                    label="Registration Number"
                    value={associationData.registrationNumber}
                  />
                  <InfoRow
                    icon={<Email fontSize="small" />}
                    label="Email"
                    value={associationData.email}
                  />
                  <InfoRow
                    icon={<Phone fontSize="small" />}
                    label="Contact"
                    value={associationData.contactNumber}
                  />
                  <InfoRow
                    icon={<CalendarToday fontSize="small" />}
                    label="Last Election Date"
                    value={
                      associationData.lastElectionDate
                        ? new Date(
                            associationData.lastElectionDate
                          ).toLocaleDateString('en-IN')
                        : 'N/A'
                    }
                  />
                  <InfoRow
                    icon={<AccountBalance fontSize="small" />}
                    label="Registered At"
                    value={
                      associationData.registeredAt
                        ? new Date(
                            associationData.registeredAt
                          ).toLocaleDateString('en-IN')
                        : 'N/A'
                    }
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Status Card */}
          <Grid item xs={12} md={4}>
            <InfoCard>
              <CardContent>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      margin: '0 auto 16px',
                      backgroundColor: '#291e6c',
                    }}
                  >
                    <Business sx={{ fontSize: 40 }} />
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      mb: 1,
                      fontFamily: "'Oswald', sans-serif",
                    }}
                  >
                    Status
                  </Typography>
                  <Chip
                    icon={verificationStatus.icon}
                    label={verificationStatus.label}
                    color={verificationStatus.color}
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      mb: 2,
                    }}
                  />
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      Registration Number
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      {associationData.registrationNumber}
                    </Typography>

                    {associationData.verificationDate && (
                      <>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ mb: 1 }}
                        >
                          Verified On
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {new Date(
                            associationData.verificationDate
                          ).toLocaleDateString('en-IN')}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>
        </Grid>
      </StyledPaper>

      {/* Office Bearers Section */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: '#291e6c',
            fontFamily: "'Oswald', sans-serif",
            mb: 2,
          }}
        >
          Office Bearers
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <OfficeBearerCard
            title="President"
            data={associationData.president}
            icon={<Person />}
            expanded={expandedSections.president}
            onToggle={() => toggleSection('president')}
          />
        </Grid>
        <Grid item xs={12}>
          <OfficeBearerCard
            title="Secretary"
            data={associationData.secretary}
            icon={<Person />}
            expanded={expandedSections.secretary}
            onToggle={() => toggleSection('secretary')}
          />
        </Grid>
        <Grid item xs={12}>
          <OfficeBearerCard
            title="Treasurer"
            data={associationData.treasurer}
            icon={<Person />}
            expanded={expandedSections.treasurer}
            onToggle={() => toggleSection('treasurer')}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfileSection;